import React from "react";
import {
  Modal,
  Stack,
  Text,
  Select,
  Group,
  Button,
  CopyButton,
  Paper,
  TextInput,
  ActionIcon,
  Tooltip,
  Badge,
  Image,
  Divider,
  ThemeIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconQrcode,
  IconExternalLink,
  IconForms,
  IconDownload,
  IconShare,
} from "@tabler/icons-react";

interface ShareLandingPageModalProps {
  opened: boolean;
  onClose: () => void;
  savedPageUrl: string;
  availableForms: { value: string; label: string }[];
  selectedFormIdUrl: string | null;
  onSelectFormIdUrl: (val: string | null) => void;
}

export const ShareLandingPageModal: React.FC<ShareLandingPageModalProps> = ({
  opened,
  onClose,
  savedPageUrl,
  availableForms,
  selectedFormIdUrl,
  onSelectFormIdUrl,
}) => {
  const shareableUrl =
    selectedFormIdUrl && savedPageUrl.includes("?")
      ? `${savedPageUrl}&form_id=${selectedFormIdUrl}`
      : savedPageUrl;

  const qrDownloadUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
    shareableUrl,
  )}&size=300x300&format=png`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing="xs">
          <ThemeIcon size="md" radius="md" variant="light" color="indigo">
            <IconShare size={18} />
          </ThemeIcon>
          <div>
            <Text weight={800} size="sm" sx={{ lineHeight: 1.2 }}>
              Share Landing Page
            </Text>
            <Text size="11px" color="dimmed">
              Embed a form and distribute your public page
            </Text>
          </div>
        </Group>
      }
      centered
      size="md"
      radius="lg"
      padding="xl"
      overlayProps={{ opacity: 0.4, blur: 3 }}
    >
      <Stack spacing="lg">
        {/* Form Selector */}
        <Select
          label={
            <Group spacing={6} mb={2}>
              <IconForms size={14} color="#6366f1" />
              <Text size="xs" weight={700}>
                Connected Lead Form
              </Text>
            </Group>
          }
          placeholder="Pick a form to auto-embed (Optional)"
          data={availableForms}
          value={selectedFormIdUrl}
          onChange={onSelectFormIdUrl}
          clearable
          size="sm"
          radius="md"
        />

        {selectedFormIdUrl ? (
          <>
            {/* Link Field with Integrated Copy & Open Actions */}
            <div>
              <Group position="apart" mb={4}>
                <Text size="xs" weight={700} color="#334155">
                  Public Page URL
                </Text>
                <Badge size="xs" variant="dot" color="indigo">
                  Form Attached
                </Badge>
              </Group>

              <TextInput
                readOnly
                value={shareableUrl}
                size="sm"
                radius="md"
                styles={{
                  input: {
                    backgroundColor: "#f8fafc",
                    borderColor: "#e2e8f0",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  },
                }}
                rightSection={
                  <Group spacing={4} pr={6}>
                    <CopyButton value={shareableUrl}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied!" : "Copy Link"}
                          withArrow
                        >
                          <ActionIcon
                            size="sm"
                            color={copied ? "teal" : "gray"}
                            variant={copied ? "filled" : "subtle"}
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck size={14} />
                            ) : (
                              <IconCopy size={14} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                    <Tooltip label="Open Preview" withArrow>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="indigo"
                        component="a"
                        href={shareableUrl}
                        target="_blank"
                      >
                        <IconExternalLink size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                }
              />
            </div>

            <Divider />

            {/* QR Code Section */}
            {savedPageUrl && (
              <Paper
                p="md"
                withBorder
                radius="md"
                sx={{
                  backgroundColor: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <Group spacing="md" noWrap>
                  <Paper
                    p={4}
                    withBorder
                    radius="sm"
                    sx={{
                      backgroundColor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Image
                      src={qrDownloadUrl}
                      alt="QR Code"
                      width={64}
                      height={64}
                      fit="contain"
                    />
                  </Paper>
                  <div>
                    <Text size="xs" weight={700}>
                      Scan & Download QR
                    </Text>
                    <Text size="11px" color="dimmed" mt={2}>
                      300×300 PNG ready for posters and stands
                    </Text>
                  </div>
                </Group>

                <Button
                  variant="light"
                  color="indigo"
                  size="xs"
                  radius="md"
                  leftIcon={<IconDownload size={14} />}
                  component="a"
                  href={qrDownloadUrl}
                  download="landing-page-qr.png"
                  target="_blank"
                >
                  Save QR
                </Button>
              </Paper>
            )}
          </>
        ) : (
          <Paper
            p="lg"
            withBorder
            radius="md"
            sx={{ backgroundColor: "#f8fafc", textAlign: "center" }}
          >
            <ThemeIcon
              size="xl"
              radius="xl"
              variant="light"
              color="indigo"
              mb="sm"
            >
              <IconForms size={24} />
            </ThemeIcon>
            <Text size="sm" weight={600} color="#334155">
              Select a Form First
            </Text>
            <Text size="xs" color="dimmed" mt={4}>
              Please select a connected lead form above to generate your
              shareable URL and QR code.
            </Text>
          </Paper>
        )}
      </Stack>
    </Modal>
  );
};
